'use client';

/**
 * Root error boundary. Replaces the entire root layout when rendering
 * crashes above the dashboard segment, so it must render its own
 * <html>/<body> and stay dependency-light (no providers are mounted here).
 * Token vars carry fallbacks because themes.css may not have loaded.
 */

import { useEffect } from 'react';
import { reportError } from '@/lib/telemetry';
import styles from './global-error.module.css';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    reportError(error, { boundary: 'global-error', digest: error.digest });
  }, [error]);

  return (
    <html lang="es">
      <body className={styles.body}>
        <main className={styles.card}>
          <h1 className={styles.title}>Something went wrong</h1>
          <p className={styles.subtitle}>Algo salió mal</p>
          <p className={styles.detail}>
            The error has been reported. You can try again, or reload the page
            if the problem persists.
            {error.digest ? ` (ref: ${error.digest})` : null}
          </p>
          <div className={styles.actions}>
            <button type="button" className={styles.retryButton} onClick={() => reset()}>
              Try again / Reintentar
            </button>
            {/* Deliberate full-page anchor (not <Link>): after a root render
                crash the router state is not trustworthy; a hard navigation
                resets it. */}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a className={styles.homeLink} href="/">
              Go to home / Ir al inicio
            </a>
          </div>
        </main>
      </body>
    </html>
  );
}
